import $ from "jquery";
import axios from "axios";

const CARD_GRIDS = ".ms_cards[data-equalizer], .ms_cards [data-equalizer]";

/**
 * Take equalizing over from Foundation on the card grids.
 *
 * Equalizer waits for every image before its first pass, and it binds to both
 * `resizeme.zf.trigger` and `mutateme.zf.trigger` — the latter observes `style`
 * with `subtree: true`, so an animation rewriting inline styles costs a reflow
 * per frame, each one dropping heights to `auto` before measuring.
 *
 * Only a real resize changes what the heights should be. Runs again after
 * `Foundation.reInit('equalizer')`, which binds both listeners back.
 */
function takeOverEqualizing() {
    $(CARD_GRIDS).each(function () {
        const $grid = $(this);
        const plugin = $grid.data("zfPlugin");

        if (!plugin || typeof plugin._reflow !== "function") {
            return;
        }

        $grid.off("resizeme.zf.trigger mutateme.zf.trigger");

        // reInit keeps the same plugin instance, so one resize handler per grid
        // stays correct however often this runs.
        if (!$grid.data("soilEqualized")) {
            $grid.data("soilEqualized", true);

            let pending;
            $(window).on("resize.soilCards", () => {
                clearTimeout(pending);
                pending = setTimeout(() => plugin._reflow(), 100);
            });
        }

        plugin._reflow();
    });
}

$(() => {
    // After the ready handler that calls $(document).foundation(), so the plugin
    // instances exist to be reflowed.
    requestAnimationFrame(takeOverEqualizing);
});

/**
 * The containers a list-view block keeps its items in.
 *
 * A block can hold more than one: with `show_as_accordion_on_mobile` the same
 * items are rendered a second time inside the accordion, and both copies have
 * to grow when more are loaded or the mobile view goes stale.
 *
 * The vertical layout nests its items one level deeper than the horizontal one,
 * which is why the inner wrapper is preferred where it exists.
 */
function listContainers(root) {
    return Array.from(root.querySelectorAll(".list-view")).map(
        (view) => view.querySelector(".quick-navigation") ?? view,
    );
}

/**
 * The items already in such a container, minus the list title.
 *
 * Unlike a grid card, a list item carries no class of its own - it is whatever
 * the item partial emitted, usually a single `<a>`. So items are taken as the
 * container's children and the optional title, the one child that is not an
 * item, is filtered out. Only renumbering needs this; the items arriving from
 * the server come without a title around them.
 */
function listItems(container) {
    return Array.from(container.children).filter((el) => !el.matches("span.title, a.link"));
}

/**
 * Renumber a numbered list after appending to it.
 *
 * Every AJAX render starts counting at one, so without this a second page of a
 * numbered list starts over at 1 halfway down.
 */
function renumberList(container) {
    let number = 0;

    // Straight at the labels: an item with a link is one <a> wrapping its own
    // `.number`, while an item without one leaves that span a sibling of the rest.
    container.querySelectorAll(".number").forEach((label) => {
        number += 1;
        label.textContent = number;
    });
}

$(() => {
    $(".load-more").on("click", function () {
        const id = $(this).data("id");
        const block = document.getElementById(id);

        // The list view has no `.cards` wrapper and no `.item-card` elements, so the
        // mode decides how the response is read. It comes from the button, because the
        // server picks the partial from `show_as_list` and both halves have to agree:
        // a theme overriding the list partial without a `.list-view` wrapper used to
        // leave the client reading list markup as grid markup, find no `.item-card`,
        // conclude nothing had loaded and remove the button having appended nothing.
        //
        // Sniffing the markup stays the fallback, for buttons rendered before the
        // attribute existed or by a theme overriding this partial.
        const listTargets = block ? listContainers(block) : [];
        const declaredList = $(this).data("is-list");
        const isList = typeof declaredList === "boolean" ? declaredList : listTargets.length > 0;
        // Read the raw attribute rather than the jQuery-parsed object: the field set
        // is a sealed string the server opens, so it has to go back untouched.
        const fields = $(this).attr("data-fields");
        const amountToLoad = parseInt($(this).data("amount-to-load"), 10) || 0;
        if (!fields) {
            return;
        }

        // The server returns the next offset; fall back to counting the DOM only
        // until the first response has been seen.
        const storedNext = $(this).data("next-offset");
        const initialOffset = $(this).data("initial-offset") ?? 0;
        // How many items are on the page already. The button carries the count
        // the server rendered, which is the same number the server derives
        // next_offset from - so the first click now agrees with every click
        // after it, in both views.
        //
        // Counting the DOM is the fallback for markup rendered before the
        // attribute existed, or by a theme overriding the button partial. It is
        // an estimate in both views: the grid leans on the `-included` class a
        // theme may add to cards it inserted itself, and a list item carries no
        // class at all, so a separator in a horizontal list counts as an item.
        const renderedCount = parseInt($(this).data("rendered-count"), 10);
        const offset = Number.isFinite(renderedCount)
            ? renderedCount
            : isList
              ? listItems(listTargets[0]).length
              : ($(`#${id} .cards .item-card:not([class$="included"])`).length ?? 0);
        const nextOffset = typeof storedNext === "number" ? storedNext : initialOffset + offset;
        const masonry = $(this).data("is-masonry") ?? 0;
        const slider = $(this).data("is-slider") ?? 0;
        const textLoadMore = $(this).data("text-load-more");
        const textLoading = $(this).data("text-loading");
        const textLoadFailed = $(this).data("text-load-failed");

        // A list block with nowhere to append to: a theme override that replaced the
        // `.list-view` container. Loading anyway would advance the offset and drop a
        // page of posts on every click, without anything appearing.
        if (isList && listTargets.length === 0) {
            console.warn("[cards] load more: no list container found for block", id);
            $(`#${id} .load-more`).html(textLoadFailed || textLoadMore);

            return;
        }

        // Data to send back to WordPress.
        const formData = new FormData();
        formData.append("action", "wpsoup_load_posts_by_ajax");
        formData.append("security", frameworkGlobal.nonce);
        formData.append("offset", nextOffset);
        formData.append("fields", fields);
        // Only present on markup rendered before payloads were sealed: a page cached
        // back then is still served to browsers running this bundle.
        const legacySignature = $(this).attr("data-fields-signature");
        if (legacySignature) {
            formData.append("fields_signature", legacySignature);
        }

        // Change button text to loading text.
        $(`#${id} .load-more`).html(textLoading);

        // Do request.
        axios
            .post(frameworkGlobal.ajaxurl, formData)
            .then(({ data }) => {
                // The endpoint returns { html, next_offset, has_more }. Tolerate the
                // older raw-HTML shape so a cached page served with older JS still works.
                const payload = data && typeof data === "object" && data.data ? data.data : null;
                const html = payload ? payload.html : data;

                // Create a placeholder div to store retrieved posts.
                const container = document.createElement("div");
                container.innerHTML = html;

                // Extract only the item-card elements from the response, skipping
                // the mobile slider: when cards_in_slider_mobile is on the block
                // renders the same cards a second time, and appending both would
                // duplicate every card on screen.
                const newCards = isList
                    ? []
                    : Array.from(container.querySelectorAll(".item-card")).filter(
                          (el) => !el.closest(".card-mobile-slider"),
                      );

                const appended = [];
                let newCount = 0;

                if (isList) {
                    // A list block answers with the items alone - no container,
                    // no title. Nodes, not children: a horizontal list writes its
                    // separator as bare text, and an item without a link is text too.
                    const sources = Array.from(container.childNodes);
                    // One `.post-title` per item, whether or not the item is a link;
                    // a link-less item is several sibling elements, so counting
                    // elements would count one post more than once.
                    const titles = container.querySelectorAll(".post-title").length;
                    newCount =
                        titles ||
                        sources.filter((node) => node.nodeType === Node.ELEMENT_NODE)
                            .length;

                    listTargets.forEach((target, index) => {
                        // Every copy gets the same items; only the first one's
                        // clones are reported as appended, so a listener does not
                        // animate the hidden accordion copy as well.
                        sources.forEach((item) => {
                            const clone = item.cloneNode(true);
                            target.appendChild(clone);

                            if (index === 0 && clone.nodeType === Node.ELEMENT_NODE) {
                                appended.push(clone);
                            }
                        });

                        renumberList(target);
                    });
                } else {
                    newCount = newCards.length;

                    newCards.forEach((card) => {
                        const clone = card.cloneNode(true);
                        $(`#${id} .cards`).append(clone);
                        appended.push(clone);
                    });
                }

                // Keep the button's own count in step with what is on the page,
                // so a response without next_offset still advances.
                $(this).data("rendered-count", offset + newCount);

                // Prefer the server's answer; the counting heuristic below is only
                // a fallback for the old response shape. A theme override of the
                // Blade partial may not emit the amount attribute, and an empty ACF
                // field yields 0, so treat "unknown" as "keep the button".
                if (payload && typeof payload.next_offset === "number") {
                    $(this).data("next-offset", payload.next_offset);
                }

                const done = payload
                    ? payload.has_more === false
                    : newCount === 0 || (amountToLoad > 0 && newCount < amountToLoad);

                if (done) {
                    $(`#${id} .load-more-container`).remove();
                } else {
                    // Update button text back to load more
                    $(`#${id} .load-more`).html(textLoadMore);
                }

                // Reinit masonry or equalizer.
                if (slider) {
                    const instance = cardSliders.find((x) => x.id === id).instance;
                    instance.update();
                }

                // Reinit masonry or equalizer.
                if (masonry) {
                    const container = masonryGrids.find((x) => x.id === id).container;
                    window.masonryGridPolyfill(container, true, true);
                } else {
                    Foundation.reInit("equalizer");
                    takeOverEqualizing();
                }

                // The animation block listens: its CSS hides children until the
                // animation places them, and the page just got taller.
                document.getElementById(id)?.dispatchEvent(
                    new CustomEvent("wpsoup/content/appended", {
                        bubbles: true,
                        detail: { elements: appended },
                    }),
                );
            })
            .catch((error) => {
                $(`#${id} .load-more`).html(textLoadFailed);
            });
    });
});
